import { useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { newFolderHint } from "../../utils/pathRules";

export interface NewFolderDialogProps {
  open: boolean;
  /** Where the new folder is created; "" is the mount's own root. */
  parentPath: string;
  onClose: () => void;
  onCreate: (name: string) => Promise<void>;
}

/**
 * ADR 0010 section 6: client-side hints mirroring
 * `sharing.ValidateFolderName` while typing (the server stays
 * authoritative; a 409 from the actual submit, folder_exists or
 * folder_limit_reached, shows here too).
 */
export function NewFolderDialog({ open, parentPath, onClose, onCreate }: NewFolderDialogProps) {
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (!open) {
      setName("");
      setError(null);
    }
  }

  const hint = newFolderHint(parentPath, name);

  async function handleCreate() {
    if (name === "" || hint) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onCreate(name);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not create the folder.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="new-folder-title">
      <DialogTitle id="new-folder-title">New folder</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <DialogContentText sx={{ mb: 2 }}>
          {parentPath ? `Creates a folder inside "${parentPath}".` : "Creates a folder here."}
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          label="Folder name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={name !== "" && hint !== null}
          helperText={name !== "" ? hint : undefined}
          slotProps={{ htmlInput: { "aria-label": "Folder name" } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleCreate()}
          variant="contained"
          disabled={submitting || name === "" || hint !== null}
        >
          Create folder
        </Button>
      </DialogActions>
    </Dialog>
  );
}
